(() => {
  const configEl = document.getElementById('dr-config');
  const config = configEl ? JSON.parse(configEl.textContent) : {};
  const DR = (window.DR = window.DR || {});
  DR.config = config;

  const locale = config.locale || 'en-GB';
  const currency = config.currency || 'GBP';
  const strings = config.strings || {};

  DR.t = (key, vars = {}) => {
    let text = strings[key] ?? key;
    Object.entries(vars).forEach(([name, value]) => {
      text = text.split(`__${name.toUpperCase()}__`).join(String(value));
    });
    return text;
  };

  const pluralRules = new Intl.PluralRules(locale);
  DR.plural = (key, count) => {
    const form = `${key}_${pluralRules.select(count)}`;
    return DR.t(form in strings ? form : `${key}_other`, { count });
  };

  DR.formatMoney = (amount, options = {}) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency, ...options }).format(amount);

  DR.formatCents = (cents) => DR.formatMoney(Number(cents) / 100);

  const round2 = (n) => Math.round(n * 100) / 100;

  DR.onVisible = (element, callback, rootMargin = '400px 0px') => {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          callback();
        }
      },
      { rootMargin }
    );
    observer.observe(element);
  };

  DR.loadScript = (src, attributes = {}) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) return existing._drPromise || Promise.resolve();
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    Object.entries(attributes).forEach(([key, value]) => script.setAttribute(key, value));
    script._drPromise = new Promise((resolve, reject) => {
      script.onload = () => resolve();
      script.onerror = () => {
        script.remove();
        reject(new Error(`Failed to load ${src}`));
      };
    });
    document.head.appendChild(script);
    return script._drPromise;
  };

  DR.toast = (message, type = 'info') => {
    const region = document.querySelector('[data-toast-region]');
    if (!region || !message) return;
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.textContent = message;
    region.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  };

  DR.openDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    // Keep the dialog itself focusable so showModal() does not dump focus onto
    // the first link (e.g. the mobile-menu logo) and flash a focus ring.
    if (!dialog.hasAttribute('tabindex')) dialog.tabIndex = -1;
    dialog.showModal();
    dialog.focus({ preventScroll: true });
    document.documentElement.classList.add('has-dialog');
  };

  DR.closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('close', (event) => {
    if (event.target instanceof HTMLDialogElement && !document.querySelector('dialog[open]')) {
      document.documentElement.classList.remove('has-dialog');
    }
  }, true);

  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      DR.closeDialog(closer.closest('dialog'));
      return;
    }
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      DR.openDialog(document.getElementById(opener.dataset.dialogOpen));
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-light-dismiss')) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) DR.closeDialog(event.target);
    }
  });

  const cartCountEls = () => document.querySelectorAll('[data-cart-count]');

  const setCartCount = (count) => {
    cartCountEls().forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-link]').forEach((link) => {
      link.setAttribute('aria-label', DR.plural('basketCount', count));
    });
  };

  DR.cart = {
    async refresh() {
      const response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      setCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('dr:cart-updated', { detail: { cart } }));
      return cart;
    },
    async add(items) {
      const response = await fetch(`${config.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || DR.t('cartError'));
      await DR.cart.refresh();
      return data;
    },
    async change(key, quantity, sections) {
      const body = { id: key, quantity };
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      const response = await fetch(`${config.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || DR.t('cartError'));
      setCartCount(data.item_count);
      document.dispatchEvent(new CustomEvent('dr:cart-updated', { detail: { cart: data } }));
      return data;
    },
    async update(updates, sections) {
      const body = { updates };
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      const response = await fetch(`${config.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || DR.t('cartError'));
      setCartCount(data.item_count);
      document.dispatchEvent(new CustomEvent('dr:cart-updated', { detail: { cart: data } }));
      return data;
    },
  };

  const WISHLIST_KEY = 'dr-wishlist';

  const readWishlist = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || 'null');
      const items = raw?.state?.items ?? raw?.items ?? [];
      return Array.isArray(items) ? items.filter((item) => item && item.handle) : [];
    } catch {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify({ state: { items }, version: 0 }));
    } catch {
      return;
    }
    syncWishlist();
    document.dispatchEvent(new CustomEvent('dr:wishlist-updated', { detail: { items } }));
  };

  const syncWishlist = () => {
    const items = readWishlist();
    const handles = new Set(items.map((item) => item.handle));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = handles.has(button.dataset.handle);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? DR.t('wishlistRemove') : DR.t('wishlistAdd'));
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = String(items.length);
      el.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-wishlist-count-link]').forEach((link) => {
      link.setAttribute('aria-label', DR.plural('favouritesCount', items.length));
    });
  };

  DR.wishlist = {
    items: readWishlist,
    has: (handle) => readWishlist().some((item) => item.handle === handle),
    add(item) {
      const items = readWishlist();
      if (items.some((entry) => entry.handle === item.handle)) return;
      writeWishlist([{ ...item, addedAt: Date.now() }, ...items]);
    },
    remove(handle) {
      writeWishlist(readWishlist().filter((item) => item.handle !== handle));
    },
    toggle(item) {
      if (DR.wishlist.has(item.handle)) {
        DR.wishlist.remove(item.handle);
        return false;
      }
      DR.wishlist.add(item);
      return true;
    },
    clear() {
      writeWishlist([]);
    },
    sync: syncWishlist,
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    let item = {};
    try {
      item = JSON.parse(button.dataset.item || '{}');
    } catch {
      item = {};
    }
    item.handle = button.dataset.handle;
    const added = DR.wishlist.toggle(item);
    DR.toast(added ? DR.t('wishlistAdded') : DR.t('wishlistRemoved'));
  });

  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) syncWishlist();
  });

  const heartIcon =
    '<svg class="icon wishlist-button__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const productImageUrl = (product) => {
    const featured = product.featured_image;
    if (typeof featured === 'string' && featured) return featured;
    if (featured && typeof featured === 'object') return featured.src || featured.url || '';
    const first = product.images?.[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object') return first.src || first.url || '';
    return '';
  };

  const buildWishlistItemJson = (product) => {
    const variant = product.variants?.[0];
    const imageUrl = productImageUrl(product);
    const priceAmount = variant ? (variant.price / 100).toFixed(2) : '0';
    const compare = variant?.compare_at_price;
    const item = {
      productId: `gid://shopify/Product/${product.id}`,
      title: product.title,
      imageUrl,
      imageAlt: product.title,
      price: { amount: priceAmount, currencyCode: currency },
      compareAtPrice:
        compare && compare > variant.price
          ? { amount: (compare / 100).toFixed(2), currencyCode: currency }
          : null,
    };
    return JSON.stringify(item);
  };

  const renderFavouritesCard = (product) => {
    const variant = product.variants?.[0];
    const imageUrl = productImageUrl(product);
    const itemJson = escapeHtml(buildWishlistItemJson(product));
    const price = variant ? DR.formatCents(variant.price) : '';
    const compare = variant?.compare_at_price;
    const compareHtml =
      compare && compare > variant.price
        ? `<span class="main-favourites-card__compare">${DR.formatCents(compare)}</span>`
        : '';

    const media = imageUrl
      ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" class="main-favourites-card__image" loading="lazy" width="600" height="600">`
      : `<span class="main-favourites-card__placeholder">${escapeHtml(DR.t('favouritesNoImage'))}</span>`;

    return `<article class="main-favourites-card product-card">
      <button type="button" class="wishlist-button product-card__wishlist" aria-pressed="true" aria-label="${escapeHtml(DR.t('wishlistRemove'))}" data-wishlist-toggle data-handle="${escapeHtml(product.handle)}" data-item="${itemJson}">${heartIcon}</button>
      <a href="${escapeHtml(product.url)}" class="main-favourites-card__link">
        <span class="main-favourites-card__media">${media}</span>
        <span class="main-favourites-card__title">${escapeHtml(product.title)}</span>
        <span class="main-favourites-card__price">${compareHtml}<span>${escapeHtml(price)}</span></span>
      </a>
    </article>`;
  };

  const renderFavouritesPage = async (root) => {
    const grid = root.querySelector('[data-favourites-grid]');
    const empty = root.querySelector('[data-favourites-empty]');
    const loading = root.querySelector('[data-favourites-loading]');
    const actions = root.querySelector('[data-favourites-actions]');
    const countEl = root.querySelector('[data-favourites-count]');
    if (!grid || !empty || !loading) return;

    const stored = readWishlist();
    const handles = stored.map((item) => item.handle).filter(Boolean);

    if (countEl) {
      countEl.textContent = handles.length ? DR.plural('favouritesCountLabel', handles.length) : '';
    }

    if (!handles.length) {
      loading.hidden = true;
      grid.hidden = true;
      if (actions) actions.hidden = true;
      empty.hidden = false;
      grid.replaceChildren();
      return;
    }

    loading.hidden = false;
    empty.hidden = true;
    grid.hidden = true;
    if (actions) actions.hidden = true;

    const products = [];
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          if (!response.ok) throw new Error('missing');
          products.push(await response.json());
        } catch {
          /* skip unavailable products */
        }
      }),
    );

    loading.hidden = true;

    if (!products.length) {
      empty.hidden = false;
      grid.hidden = true;
      return;
    }

    const order = new Map(handles.map((handle, index) => [handle, index]));
    products.sort((a, b) => (order.get(a.handle) ?? 0) - (order.get(b.handle) ?? 0));

    grid.innerHTML = products.map((product) => `<li>${renderFavouritesCard(product)}</li>`).join('');
    grid.hidden = false;
    if (actions) actions.hidden = false;
    syncWishlist();
  };

  const initFavouritesPage = (root) => {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    renderFavouritesPage(root);
    root.querySelector('[data-favourites-clear]')?.addEventListener('click', () => {
      writeWishlist([]);
      renderFavouritesPage(root);
    });
  };

  DR.initFavouritesPage = initFavouritesPage;

  document.addEventListener('dr:wishlist-updated', () => {
    document.querySelectorAll('[data-favourites-page]').forEach((root) => {
      if (root.dataset.ready) renderFavouritesPage(root);
    });
  });

  const firstNum = (source, keys) => {
    if (!source) return null;
    for (const key of keys) {
      const raw = source[key];
      const num = typeof raw === 'string' ? parseFloat(raw) : typeof raw === 'number' ? raw : NaN;
      if (Number.isFinite(num)) return num;
    }
    return null;
  };

  const amortise = (credit, apr, months) => {
    if (!months) return 0;
    if (!apr) return round2(credit / months);
    const monthlyRate = Math.pow(1 + apr / 100, 1 / 12) - 1;
    return round2((credit * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months)));
  };

  const buildFinanceOptions = (products, cashPrice, deposit) => {
    const credit = round2(cashPrice - deposit);
    return products
      .map((product, index) => {
        const months = firstNum(product, ['term', 'months', 'numberOfPayments', 'duration']) ?? 0;
        const apr = firstNum(product, ['apr', 'representativeApr', 'APR']) ?? 0;
        const productId = String(product.productId ?? product.id ?? product.financeProductId ?? index);
        let pay;
        try {
          if (window.V12?.getPaymentBreakdown) pay = window.V12.getPaymentBreakdown(productId, cashPrice, deposit);
          else if (window.V12?.calculate) pay = window.V12.calculate(productId, cashPrice, deposit);
        } catch {
          pay = undefined;
        }
        const monthly = firstNum(pay, ['initialPayments', 'monthlyPayment', 'monthly']) ?? amortise(credit, apr, months);
        /*
          Monthly figures are rounded to the penny. The last instalment usually
          absorbs the remainder (e.g. 23×£19.80 + £19.90). Multiplying
          monthly×months drops that 10p and produced "-£0.10 interest".
        */
        let finalPayment = firstNum(pay, ['finalPayment', 'finalPayments', 'lastPayment', 'finalInstalment']);

        let totalRepayable = firstNum(pay, ['amountPayable', 'totalPayable', 'totalRepayable']);
        if (totalRepayable == null) {
          if (finalPayment != null && months > 1) {
            totalRepayable = round2(monthly * (months - 1) + finalPayment + deposit);
          } else if (apr === 0) {
            totalRepayable = cashPrice;
          } else {
            totalRepayable = round2(monthly * months + deposit);
          }
        }

        let totalInterest = firstNum(pay, ['interest', 'totalInterestPayable']);
        if (totalInterest == null) {
          totalInterest = round2(totalRepayable - cashPrice);
        }

        // Interest-free totals must equal cash price; never show negative interest from rounding
        if (apr === 0) {
          totalRepayable = cashPrice;
          totalInterest = 0;
        } else if (Math.abs(totalInterest) < 0.005) {
          totalInterest = 0;
        }

        // Derive the final instalment when V12 didn't supply one
        if ((finalPayment == null || !Number.isFinite(finalPayment)) && months > 1 && monthly > 0) {
          finalPayment = round2(round2(totalRepayable - deposit) - monthly * (months - 1));
        }
        if (finalPayment != null && finalPayment <= 0) finalPayment = null;

        return {
          productId,
          months,
          apr,
          monthly,
          finalPayment,
          deposit,
          cashPrice,
          credit,
          totalRepayable,
          totalInterest,
          annualRate: firstNum(pay ?? product, ['annualRate', 'interestRate', 'annualInterestRate', 'nominalRate']),
        };
      })
      .filter((option) => option.months > 0 && option.monthly > 0)
      .sort((a, b) => a.months - b.months);
  };

  const v12Config = config.v12 || {};

  DR.v12 = {
    enabled: Boolean(v12Config.retailerId && v12Config.retailerGuid),
    depositPercent: Number(v12Config.deposit) || 20,
    teaserMonths: Number(v12Config.teaserMonths) || 24,
    load() {
      if (!DR.v12.enabled) return Promise.reject(new Error('V12 not configured'));
      if (window.V12) return Promise.resolve();
      return DR.loadScript(
        `https://apply.v12finance.com/JavascriptGateway/JSCalc/CalculatorScript?retailerId=${encodeURIComponent(
          v12Config.retailerId
        )}&retailerGuid=${encodeURIComponent(v12Config.retailerGuid)}`
      );
    },
    deposit: (price) => round2((price * DR.v12.depositPercent) / 100),
    async options(price) {
      await DR.v12.load();
      const products = window.V12?.getFinanceProducts?.() ?? [];
      return buildFinanceOptions(products, price, DR.v12.deposit(price));
    },
    interestFree: (options, months) => options.find((option) => option.months === months && option.apr === 0) ?? null,
    lowest: (options) =>
      options.length ? options.reduce((best, option) => (option.monthly < best.monthly ? option : best)) : null,
  };

  const renderTeaser = async (el) => {
    const price = parseFloat(el.dataset.price);
    if (!DR.v12.enabled || !(price > 0)) return;
    try {
      const options = await DR.v12.options(price);
      const option = DR.v12.interestFree(options, DR.v12.teaserMonths);
      if (!option) return;
      el.textContent = DR.t('financeTeaser', { amount: DR.formatMoney(option.monthly), months: option.months });
      el.hidden = false;
    } catch {
      el.hidden = true;
    }
  };

  const renderFinanceTrigger = async (el) => {
    const price = parseFloat(el.dataset.price);
    const compact = el.hasAttribute('data-compact');
    const label = el.querySelector('[data-v12-label]');
    const loading = el.querySelector('[data-v12-loading]');
    const fallback = el.querySelector('[data-v12-fallback]');
    const button = el.querySelector('[data-v12-button]');
    const done = (state) => {
      if (loading) loading.hidden = true;
      if (state === 'fail') {
        if (fallback && !compact) fallback.hidden = false;
        if (compact) el.hidden = true;
        return;
      }
      if (button) button.hidden = false;
    };
    if (!DR.v12.enabled || !(price > 0)) {
      done('fail');
      return;
    }
    el.hidden = false;
    if (loading && !el.hasAttribute('data-v12-prerendered')) loading.hidden = false;
    try {
      const options = await DR.v12.options(price);
      const interestFree = DR.v12.interestFree(options, DR.v12.teaserMonths);
      if (!options.length || !interestFree) {
        done('fail');
        return;
      }
      if (label) {
        label.textContent = DR.t('financeFrom', {
          amount: DR.formatMoney(interestFree.monthly),
          months: interestFree.months,
          deposit: DR.v12.depositPercent,
        });
      }
      el._financeOptions = options;
      el._financePrice = price;
      done('ok');
    } catch {
      done('fail');
    }
  };

  const financeDialog = () => document.getElementById('V12Dialog');

  const renderFinancePanel = (root, options, price, selectedId) => {
    if (!root) return;
    const deposit = DR.v12.deposit(price);
    const selected =
      options.find((option) => option.productId === selectedId) ?? DR.v12.lowest(options) ?? options[0];
    const description = root.querySelector('[data-v12-description]');
    if (description) {
      description.textContent = DR.t('financeDescription', {
        price: DR.formatMoney(price),
        percent: DR.v12.depositPercent,
        deposit: DR.formatMoney(deposit),
      });
    }
    const body = root.querySelector('[data-v12-body]');
    const empty = root.querySelector('[data-v12-empty]');
    if (!selected) {
      if (body) body.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }
    if (body) body.hidden = false;
    if (empty) empty.hidden = true;
    const monthly = root.querySelector('[data-v12-monthly]');
    if (monthly) monthly.textContent = DR.formatMoney(selected.monthly);
    const summary = root.querySelector('[data-v12-summary]');
    if (summary) {
      summary.textContent = DR.t('financeSummary', {
        months: selected.months,
        apr: selected.apr.toFixed(1),
      });
    }
    const terms = root.querySelector('[data-v12-terms]');
    if (terms) {
      terms.replaceChildren(
        ...options.map((option) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'v12-term';
          button.setAttribute('aria-pressed', String(option.productId === selected.productId));
          const months = document.createElement('span');
          months.className = 'v12-term__months';
          months.textContent = DR.t('financeMonths', { months: option.months });
          const apr = document.createElement('span');
          apr.className = 'v12-term__apr';
          apr.textContent = DR.t('financeApr', { apr: option.apr.toFixed(1) });
          button.append(months, apr);
          button.addEventListener('click', () => {
            root._selectedFinanceId = option.productId;
            renderFinancePanel(root, options, price, option.productId);
          });
          return button;
        })
      );
    }
    const rows = [
      [DR.t('financeCashPrice'), DR.formatMoney(selected.cashPrice)],
      [DR.t('financeDeposit'), DR.formatMoney(selected.deposit)],
      [DR.t('financeCredit'), DR.formatMoney(selected.credit)],
      [
        DR.t('financeRepayment'),
        selected.finalPayment != null &&
        selected.months > 1 &&
        Math.abs(selected.finalPayment - selected.monthly) >= 0.01
          ? DR.t('financeRepaymentValueFinal', {
              months: selected.months - 1,
              amount: DR.formatMoney(selected.monthly),
              final: DR.formatMoney(selected.finalPayment),
            })
          : DR.t('financeRepaymentValue', {
              months: selected.months,
              amount: DR.formatMoney(selected.monthly),
            }),
      ],
      [DR.t('financeAprLabel'), `${selected.apr.toFixed(1)}%`],
      [
        DR.t('financeAnnualRate'),
        DR.t('financeFixed', { rate: (selected.annualRate != null ? selected.annualRate : selected.apr).toFixed(2) }),
      ],
      [
        DR.t('financeTotal'),
        DR.t('financeTotalValue', {
          total: DR.formatMoney(selected.totalRepayable),
          interest: DR.formatMoney(selected.totalInterest),
        }),
        true,
      ],
    ];
    const breakdown = root.querySelector('[data-v12-breakdown]');
    if (breakdown) {
      breakdown.replaceChildren(
        ...rows.map(([label, value, emphasis]) => {
          const row = document.createElement('div');
          row.className = `v12-row${emphasis ? ' v12-row--emphasis' : ''}`;
          const l = document.createElement('span');
          l.textContent = label;
          const v = document.createElement('span');
          v.textContent = value;
          row.append(l, v);
          return row;
        })
      );
    }
  };

  const renderFinanceDialog = (dialog, options, price, selectedId) => {
    renderFinancePanel(dialog, options, price, selectedId);
  };

  const clampFinancePrice = (value, min, max) => {
    let price = Number(value);
    if (!Number.isFinite(price)) price = min;
    if (price < min) price = min;
    if (price > max) price = max;
    return round2(price);
  };

  const initPageCalculator = (el) => {
    const input = el.querySelector('[data-v12-price-input]');
    const loading = el.querySelector('[data-v12-loading]');
    const min = Number(el.dataset.min) || 500;
    const max = Number(el.dataset.max) || 15000;
    if (!input) return;

    let timer = 0;
    let requestId = 0;
    const setLoading = (active) => {
      if (!loading) return;
      loading.hidden = !active;
    };

    const refresh = async ({ commit = false } = {}) => {
      const raw = Number(input.value);
      if (!commit && (!Number.isFinite(raw) || raw < min || raw > max)) return;
      const price = clampFinancePrice(input.value, min, max);
      if (commit && String(price) !== input.value) input.value = String(price);
      if (price === el._financePrice && el._financeOptions) return;
      if (!DR.v12.enabled) {
        renderFinancePanel(el, [], price);
        return;
      }
      const request = ++requestId;
      setLoading(true);
      try {
        const options = await DR.v12.options(price);
        if (request !== requestId) return;
        el._financeOptions = options;
        el._financePrice = price;
        renderFinancePanel(el, options, price, el._selectedFinanceId);
      } catch {
        if (request !== requestId) return;
        renderFinancePanel(el, [], price);
      } finally {
        if (request === requestId) setLoading(false);
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        el._selectedFinanceId = null;
        refresh();
      }, 280);
    });
    input.addEventListener('change', () => {
      window.clearTimeout(timer);
      el._selectedFinanceId = null;
      refresh({ commit: true });
    });
    refresh({ commit: true });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-v12-button]');
    if (!trigger) return;
    const host = trigger.closest('[data-v12-finance]');
    const dialog = financeDialog();
    if (!host || !dialog) return;
    const open = () => {
      if (!host._financeOptions) return;
      renderFinanceDialog(dialog, host._financeOptions, host._financePrice);
      DR.openDialog(dialog);
    };
    if (host._financeOptions) open();
    else {
      host._financeReady = host._financeReady ?? renderFinanceTrigger(host);
      host._financeReady.then(open);
    }
  });

  DR.initFinance = (root = document) => {
    root.querySelectorAll('[data-v12-teaser]:not([data-v12-ready])').forEach((el) => {
      el.setAttribute('data-v12-ready', '');
      DR.onVisible(el.hidden ? el.parentElement : el, () => renderTeaser(el));
    });
    root.querySelectorAll('[data-v12-finance]:not([data-v12-ready])').forEach((el) => {
      el.setAttribute('data-v12-ready', '');
      DR.onVisible(el, () => {
        el._financeReady = el._financeReady ?? renderFinanceTrigger(el);
      });
    });
    root.querySelectorAll('[data-v12-page-calculator]:not([data-v12-ready])').forEach((el) => {
      el.setAttribute('data-v12-ready', '');
      DR.onVisible(el, () => initPageCalculator(el));
    });
  };

  const KLARNA_SRC = 'https://js.klarna.com/web-sdk/v1/klarna.js';

  DR.klarna = {
    refresh() {
      window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
      window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
    },
    init(root = document) {
      // App blocks (e.g. Klarna On-site Messaging) load their own SDK, so only manage the theme's placements.
      const placements = [...root.querySelectorAll('klarna-placement[data-theme-placement]')];
      if (!placements.length || !config.klarna?.clientId) return;
      const wrappers = placements.map((el) => el.parentElement).filter(Boolean);

      // Wrappers get [data-klarna-rendered] for CSS that hides empty placements.
      // Reveal first so Klarna can layout, then collapse again if it stays empty.
      const reveal = () => wrappers.forEach((el) => el.setAttribute('data-klarna-rendered', ''));
      const collapse = () => wrappers.forEach((el) => el.removeAttribute('data-klarna-rendered'));
      const hasContent = () =>
        placements.some((el) => el.getBoundingClientRect().height > 0 || el.childElementCount > 0);

      reveal();
      DR.loadScript(KLARNA_SRC, { 'data-environment': 'production', 'data-client-id': config.klarna.clientId })
        .then(() => {
          DR.klarna.refresh();
          let attempts = 0;
          const check = () => {
            if (hasContent()) return;
            attempts += 1;
            if (attempts >= 10) {
              collapse();
              return;
            }
            DR.klarna.refresh();
            window.setTimeout(check, 500);
          };
          window.setTimeout(check, 300);
        })
        .catch(() => collapse());
    },
    setAmount(root, cents) {
      root.querySelectorAll('klarna-placement[data-theme-placement]').forEach((el) => el.setAttribute('data-purchase-amount', String(cents)));
      DR.klarna.refresh();
    },
  };

  const TRUSTPILOT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

  DR.initTrustpilot = (root = document) => {
    root.querySelectorAll('.trustpilot-widget:not([data-tp-ready])').forEach((widget) => {
      widget.setAttribute('data-tp-ready', '');
      DR.onVisible(
        widget,
        () => {
          DR.loadScript(TRUSTPILOT_SRC).catch(() => {});
          let tries = 0;
          const tryLoad = () => {
            if (window.Trustpilot?.loadFromElement) {
              window.Trustpilot.loadFromElement(widget, true);
              let frames = 0;
              const reveal = () => {
                if (widget.querySelector('iframe') || frames++ > 60) {
                  widget.closest('[data-tp-host]')?.classList.add('is-ready');
                  return;
                }
                setTimeout(reveal, 100);
              };
              reveal();
              return;
            }
            if (tries++ < 240) setTimeout(tryLoad, 250);
          };
          tryLoad();
        },
        '600px 0px'
      );
    });
  };

  DR.initCarousels = (root = document) => {
    root.querySelectorAll('[data-carousel]:not([data-carousel-ready])').forEach((carousel) => {
      carousel.setAttribute('data-carousel-ready', '');
      const track = carousel.querySelector('[data-carousel-track]');
      const prev = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      if (!track) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      const cardsPerStep = Number(carousel.dataset.carouselStep) || 0;
      const scrollBy = (direction) => {
        let distance = track.clientWidth * 0.9;
        const card = track.firstElementChild;
        if (cardsPerStep && card) {
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          distance = (card.offsetWidth + gap) * cardsPerStep;
        }
        track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      };
      prev?.addEventListener('click', () => scrollBy(-1));
      next?.addEventListener('click', () => scrollBy(1));
      track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) {
        new ResizeObserver(update).observe(track);
      } else {
        update();
      }
    });
  };

  const MONTHS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];
  const DAY = 86400000;

  const ordinal = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
  };

  const parseDisplayDate = (value) => {
    const match = String(value).trim().toLowerCase().replace(/\s+/g, ' ').match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)$/);
    if (!match) return null;
    const day = parseInt(match[1], 10);
    const monthIndex = MONTHS.indexOf(match[2]);
    if (monthIndex === -1 || Number.isNaN(day)) return null;
    const now = new Date();
    let date = new Date(now.getFullYear(), monthIndex, day);
    if (date.getDate() !== day) return null;
    if (date.getTime() < now.getTime() - 30 * DAY) {
      date = new Date(now.getFullYear() + 1, monthIndex, day);
      if (date.getDate() !== day) return null;
    }
    return date;
  };

  const formatDisplayDate = (date) =>
    `${date.getDate()}${ordinal(date.getDate())} ${date.toLocaleDateString('en-GB', { month: 'long' })}`;

  const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  };

  const formatInbound = (value) => {
    const parsed = parseDisplayDate(value);
    return parsed ? formatDisplayDate(parsed) : value;
  };

  const deliveryDateFromInbound = (value) => {
    const parsed = parseDisplayDate(value);
    return parsed ? formatDisplayDate(addDays(parsed, 7)) : value;
  };

  const deliveryWeeksFromInbound = (value) => {
    const parsed = parseDisplayDate(value);
    if (!parsed || parsed.getTime() < Date.now()) return null;
    const days = Math.max(0, Math.round((addDays(parsed, 7).getTime() - Date.now()) / DAY));
    const weeks = Math.max(1, Math.ceil(days / 7));
    return weeks === 1 ? DR.t('weeksOne') : DR.t('weeksOther', { weeks });
  };

  const formatRange = (values) => {
    if (!values.length) return '';
    const sorted = [...values].sort((a, b) => a - b);
    return sorted.length === 1 ? String(sorted[0]) : `${sorted[0]}-${sorted[sorted.length - 1]}`;
  };

  const addOneWeekToLeadTime = (value) => {
    const text = String(value).toLowerCase();
    const numbers = (text.match(/\d+(?:\.\d+)?/g) || []).map(Number);
    if (!numbers.length) return value;
    if (text.includes('day')) return `${formatRange(numbers.map((n) => n + 7))} days`;
    if (text.includes('month')) return `${formatRange(numbers.map((n) => n + 0.25))} months`;
    return `${formatRange(numbers.map((n) => n + 1))} weeks`;
  };

  const leadTimeToDays = (value) => {
    const text = String(value).toLowerCase();
    const numbers = (text.match(/\d+(?:\.\d+)?/g) || []).map(Number);
    const highest = numbers.length ? Math.max(...numbers) : 6;
    if (text.includes('month')) return highest * 30;
    if (text.includes('day')) return highest;
    return highest * 7;
  };

  const inStock = () => ({ kind: 'in_stock', title: DR.t('stockInStock') });

  const unavailableStatus = (variant) => {
    if (variant?.inbound) {
      const inboundDate = formatInbound(variant.inbound);
      const weeks = deliveryWeeksFromInbound(variant.inbound);
      return {
        kind: 'due_in',
        title: weeks ? DR.t('stockDeliveredWithin', { time: weeks }) : DR.t('stockDueSoon'),
        detail: DR.t('stockExpected', { date: inboundDate }),
        highlight: inboundDate,
        deliveryEstimate: deliveryDateFromInbound(variant.inbound),
      };
    }
    const leadTime = variant?.leadTime;
    const estimate = leadTime ? addOneWeekToLeadTime(leadTime) : null;
    return {
      kind: 'lead_time',
      title: estimate ? DR.t('stockLeadWithin', { time: estimate }) : DR.t('stockLeadOnRequest'),
      detail: leadTime ? DR.t('stockLeadDetail', { lead: leadTime }) : DR.t('stockLeadVaries'),
      highlight: leadTime || null,
      deliveryEstimate: estimate,
    };
  };

  const hasNoStockLabel = (tags = []) => tags.some((tag) => String(tag).toLowerCase() === 'no stock label');

  const singleStatus = (variant, tags = []) => {
    if (hasNoStockLabel(tags)) return inStock();
    if (variant && variant.qty == null) {
      if (variant.notInStock || variant.available === false) return unavailableStatus(variant);
      return inStock();
    }
    const qty = variant?.qty ?? 0;
    if (qty > 5) return inStock();
    if (qty > 0) return { kind: 'low_stock', title: DR.t('stockInStock'), detail: DR.t('stockOnly', { quantity: qty }) };
    return unavailableStatus(variant);
  };

  const bundleStatus = (components) => {
    let lowStock = false;
    let dueIn = false;
    let leadTime = false;
    let latestInbound = null;
    let latestInboundStr = '';
    let leadValue = '';
    const markUnavailable = (component) => {
      if (component.inbound) {
        dueIn = true;
        const time = parseDisplayDate(component.inbound)?.getTime();
        if (time && (latestInbound === null || time > latestInbound)) {
          latestInbound = time;
          latestInboundStr = component.inbound;
        }
      } else {
        leadTime = true;
        if (component.leadTime && !leadValue) leadValue = component.leadTime;
      }
    };
    components.forEach((component) => {
      if (component.qty == null) {
        if (component.notInStock || component.available === false) markUnavailable(component);
        return;
      }
      if (component.qty > 5) return;
      if (component.qty > 0) {
        lowStock = true;
        return;
      }
      markUnavailable(component);
    });
    if (leadTime) {
      const estimate = leadValue ? addOneWeekToLeadTime(leadValue) : null;
      return {
        kind: 'lead_time',
        title: estimate ? DR.t('stockLeadWithin', { time: estimate }) : DR.t('stockLeadOnRequest'),
        detail: leadValue ? DR.t('stockLeadDetail', { lead: leadValue }) : DR.t('stockBundleLeadVaries'),
        deliveryEstimate: estimate,
      };
    }
    if (dueIn) {
      const inboundDate = latestInboundStr ? formatInbound(latestInboundStr) : null;
      const weeks = latestInboundStr ? deliveryWeeksFromInbound(latestInboundStr) : null;
      return {
        kind: 'due_in',
        title: weeks ? DR.t('stockDeliveredWithin', { time: weeks }) : DR.t('stockDueSoon'),
        detail: inboundDate ? DR.t('stockBundleExpected', { date: inboundDate }) : DR.t('stockBundleSoon'),
        highlight: inboundDate,
        deliveryEstimate: latestInboundStr ? deliveryDateFromInbound(latestInboundStr) : null,
      };
    }
    if (lowStock) return { kind: 'low_stock', title: DR.t('stockInStock'), detail: DR.t('stockBundleLimited') };
    return inStock();
  };

  const basketStockLabel = (variant, tags, fallback) => {
    if (!variant) return fallback;
    const status = singleStatus(variant, tags);
    if (status.kind === 'in_stock') return DR.t('stockShort');
    if (status.kind === 'low_stock') return status.detail ? DR.t('stockShortLow', { detail: status.detail }) : DR.t('stockShort');
    return status.title;
  };

  const basketDeliveryLabel = (entries, emptyLabel) => {
    if (!entries.length) return emptyLabel;
    let bestScore = -1;
    let bestLabel = emptyLabel;
    entries.forEach((entry) => {
      const fallbackDays = (entry.fallbackWeeks ?? 6) * 7;
      const status = entry.variant ? singleStatus(entry.variant, entry.tags) : null;
      let score;
      let label;
      if (!status) {
        score = fallbackDays;
        label = entry.fallbackLabel;
      } else if (status.kind === 'in_stock' || status.kind === 'low_stock') {
        score = 5;
        label = DR.t('stockBasketInStock');
      } else if (status.kind === 'due_in') {
        const parsed = entry.variant.inbound ? parseDisplayDate(entry.variant.inbound) : null;
        const days = parsed ? Math.max(0, Math.round((parsed.getTime() - Date.now()) / DAY)) : fallbackDays;
        score = days + 7;
        label = status.title;
      } else {
        score = status.deliveryEstimate ? leadTimeToDays(status.deliveryEstimate) : fallbackDays;
        label = status.title;
      }
      if (score > bestScore) {
        bestScore = score;
        bestLabel = label;
      }
    });
    return bestLabel;
  };

  DR.delivery = { singleStatus, bundleStatus, basketStockLabel, basketDeliveryLabel };

  DR.renderStockStatus = (el) => {
    let data;
    try {
      data = JSON.parse(el.querySelector('script[type="application/json"]').textContent);
    } catch {
      return;
    }
    const status = data.bundle && data.bundle.length ? bundleStatus(data.bundle) : singleStatus(data.variant, data.tags);
    const title = el.querySelector('[data-status-title]');
    const detail = el.querySelector('[data-status-detail]');
    el.dataset.kind = status.kind;
    if (title) title.textContent = status.title.charAt(0).toUpperCase() + status.title.slice(1);
    if (detail) {
      detail.textContent = status.detail || '';
      detail.hidden = !status.detail;
    }
    el.removeAttribute('aria-busy');
  };

  DR.initStockStatus = (root = document) => {
    root.querySelectorAll('[data-stock-status]').forEach(DR.renderStockStatus);
  };

  DR.init = (root = document) => {
    DR.initFinance(root);
    DR.initCarousels(root);
    DR.initTrustpilot(root);
    DR.initStockStatus(root);
    DR.klarna.init(root);
    syncWishlist();
    root.querySelectorAll('[data-favourites-page]').forEach(initFavouritesPage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => DR.init());
  } else {
    DR.init();
  }

  document.addEventListener('shopify:section:load', (event) => DR.init(event.target));
})();
